import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Chip, Button, Paper,
    Table, TableHead, TableBody, TableRow, TableCell,
    Alert, CircularProgress, LinearProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { getCommissionDetail } from '../../api/commissionAmendmentApi';
import { handlePriceWithRM } from '../../utils/priceUtils';
import { convertDateTimeToDateString } from '../../utils/datetimeUtils';
import { useUser } from '../../context/userContext';
import AmendCommissionDialog from './amendCommissionDialog';
import LogViewer from '../common/logViewer';

const poppins = "'Poppins', sans-serif";

const TYPE_COLORS = {
    'PSC': '#1A8CFF',
    'DOC': '#F59E0B',
    'TOC': '#10B981',
    'MOC': '#EC4899',
    'MUC': '#7C3AED',
    'Webbey Commission': '#94A3B8',
    'Total Commission': '#6366F1',
    'Readjustment': '#8B5CF6',
};

function InfoBox({ label, value }) {
    return (
        <Box sx={{ background: '#F8FAFC', borderRadius: '8px', p: 1.5 }}>
            <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontFamily: poppins }}>{label}</Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#0F172A', fontFamily: poppins }}>{value || '—'}</Typography>
        </Box>
    );
}

// The discounts the GROUP funds, as one figure with its parts under it.
//
// Astern's discount is deliberately NOT in here: it comes off the price before the
// commission pool is worked out at all. These three come out of the pool, each from the
// share of whoever granted it — agent off PSC, upline off DOC, team off TOC — so their
// sum is exactly the number the pool is reduced by. Shown as three separate cards, that
// sum had to be done in the reader's head, and sale 2067 was reported as miscalculated
// because the agent's half was taken for the whole.
// (user 2026-10-23: "nexwave group discount = each individual discount")
function GroupDiscountBox({ agent, upline, team }) {
    const parts = [['agent', agent], ['upline', upline], ['team', team]]
        .filter(([, v]) => (Number(v) || 0) !== 0);
    const total = parts.reduce((sum, [, v]) => sum + (Number(v) || 0), 0);
    // Keep the existing over-RM1000 warning, but judge it on each part rather than the
    // total — three discounts of 400 are not the same thing as one of 1200.
    const anyOver = [agent, upline, team].some(v => (Number(v) || 0) > 1000);
    const accent = anyOver ? '#EF4444' : '#94A3B8';
    return (
        <Box sx={{ background: '#F8FAFC', borderRadius: '8px', p: 1.5, border: anyOver ? '2px solid #EF4444' : 'none' }}>
            <Typography sx={{ fontSize: '0.65rem', color: accent, fontWeight: 600, textTransform: 'uppercase', fontFamily: poppins }}>
                Nexwave Group Discount {anyOver ? '⚠️' : ''}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: anyOver ? '#EF4444' : '#0F172A', fontFamily: poppins }}>
                {parts.length ? rm(total) : '—'}
            </Typography>
            {parts.length > 0 && (
                <Typography sx={{ fontSize: '0.65rem', color: '#64748B', fontFamily: poppins, mt: 0.25 }}>
                    {parts.map(([name, v]) => `${name} ${rm(v)}`).join(' · ')}
                </Typography>
            )}
        </Box>
    );
}

// Currency for figures that may legitimately be NEGATIVE (a DOC/TOC line the Upline/Team
// discount pushed below zero). Rounds to 2 dp FIRST so a float residue can never print as
// "- RM 0.00". Sign convention ("- RM x") comes from handlePriceWithRM.
const rm = (v) => {
    const n = Math.round((Number(v) || 0) * 100) / 100;
    return handlePriceWithRM(n === 0 ? 0 : n);
};
// Negative commission is a legitimate outcome of an Upline/Team discount that exceeds the
// recipient's override (spec §8.3) — flag it in red rather than hiding or clamping it.
const negSx = (v) => (Number(v) < 0 ? { color: '#DC2626', fontWeight: 700 } : {});

function PaymentProgress({ paid, total }) {
    if (!total) return '—';
    // A negative `total` (negative commission line) still gives a meaningful ratio because
    // both sides carry the same sign. Only the BAR's width is clamped to 0–100 — CSS ignores
    // a negative width, which would silently render as a full-looking empty track.
    const pct = Math.min((paid / total) * 100, 100);
    const barPct = Math.max(0, Math.min(pct, 100));
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 100 }}>
            <LinearProgress variant="determinate" value={barPct} sx={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: pct >= 100 ? '#10B981' : '#1A8CFF', borderRadius: 3 } }} />
            <Typography sx={{ fontSize: '0.7rem', fontFamily: poppins, color: '#64748B', whiteSpace: 'nowrap' }}>{pct.toFixed(0)}%</Typography>
        </Box>
    );
}

export default function SalesCommissionDetail() {
    const { salesId } = useParams();
    const navigate = useNavigate();
    const { user } = useUser();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [amendOpen, setAmendOpen] = useState(false);

    const fetchDetail = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getCommissionDetail(salesId);
            setDetail(res.data);
        } catch (e) {
            console.error('Failed to fetch commission detail', e);
        } finally {
            setLoading(false);
        }
    }, [salesId]);

    useEffect(() => { fetchDetail(); }, [fetchDetail]);

    const canAmend = user?.roleRights?.includes('AllSalesCommList');

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!detail) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ fontFamily: poppins }}>Failed to load commission detail for Sale #{salesId}</Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/accounting/sales')} sx={{ mt: 2, fontFamily: poppins, textTransform: 'none' }}>
                    Back to Sales
                </Button>
            </Box>
        );
    }

    // API returns flat: detail.salesId, detail.customerName, detail.commissionLines, etc.
    const sale = detail;
    const commissionLines = detail.commissionLines || [];
    const pendingAmendment = detail.pendingAmendment;

    return (
        <div>
            {/* Back button */}
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/accounting/sales')}
                sx={{ mb: 2, fontFamily: poppins, textTransform: 'none', color: '#64748B', fontWeight: 500, fontSize: '0.85rem' }}
            >
                Back to Sales
            </Button>

            {/* Header Card */}
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: '14px', mb: 2, border: '1px solid #E2E8F0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography sx={{ fontFamily: poppins, fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' }, color: '#0F172A' }}>
                        Commission Detail — Sale #{sale?.salesId}
                    </Typography>
                    {sale?.scheme && (
                        <Chip label={sale.scheme} size="small" sx={{
                            height: 22, fontSize: '0.7rem', fontWeight: 600, fontFamily: poppins,
                            backgroundColor: sale.scheme === 'NEM' ? '#FEF3C7' : '#DBEAFE',
                            color: sale.scheme === 'NEM' ? '#92400E' : '#1E40AF',
                        }} />
                    )}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                    <InfoBox label="Customer" value={sale?.customerName} />
                    <InfoBox label="Agent" value={sale?.agentName} />
                    <InfoBox label="Co-Agent (Offline)" value={sale?.coAgentName} />
                    <InfoBox label="Category" value={sale?.productCategory} />
                    <InfoBox label="Original Price" value={sale?.originalPrice != null ? handlePriceWithRM(sale.originalPrice) : null} />
                    <Box sx={{ background: '#F8FAFC', borderRadius: '8px', p: 1.5, border: (sale?.discountByAstern ?? 0) > 1000 ? '2px solid #EF4444' : 'none' }}>
                        <Typography sx={{ fontSize: '0.65rem', color: (sale?.discountByAstern ?? 0) > 1000 ? '#EF4444' : '#94A3B8', fontWeight: 600, textTransform: 'uppercase', fontFamily: poppins }}>
                            Astern Discount {(sale?.discountByAstern ?? 0) > 1000 ? '⚠️' : ''}
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: (sale?.discountByAstern ?? 0) > 1000 ? '#EF4444' : '#0F172A', fontFamily: poppins }}>
                            {sale?.discountByAstern != null ? handlePriceWithRM(sale.discountByAstern) : '—'}
                        </Typography>
                    </Box>
                    <GroupDiscountBox
                        agent={sale?.discountByAgent}
                        upline={sale?.discountByUpline}
                        team={sale?.discountByTeam}
                    />
                    <InfoBox label="Final Price" value={sale?.finalPrice != null ? handlePriceWithRM(sale.finalPrice) : null} />
                    <InfoBox label="Deposit Date" value={sale?.depositOn ? convertDateTimeToDateString(sale.depositOn) : null} />
                    <InfoBox label="Sale ID" value={`#${sale?.salesId}`} />
                </Box>
            </Paper>

            {/* Pending Amendment Banner */}
            {pendingAmendment && (
                <Alert severity="warning" sx={{ mb: 2, fontFamily: poppins, fontSize: '0.85rem', borderRadius: '10px' }}>
                    There is a pending commission amendment for this sale (submitted {pendingAmendment.createdOn ? convertDateTimeToDateString(pendingAmendment.createdOn) : ''}).
                    Reason: {pendingAmendment.reason || '—'}
                </Alert>
            )}

            {/* Commission Breakdown */}
            <Paper elevation={0} sx={{ borderRadius: '14px', border: '1px solid #E2E8F0', overflow: 'hidden', mb: 2 }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography sx={{ fontFamily: poppins, fontWeight: 700, fontSize: '1rem', color: '#0F172A' }}>
                        Commission Breakdown
                    </Typography>
                    {canAmend && !pendingAmendment && (
                        <Button
                            startIcon={<EditIcon />}
                            variant="contained"
                            size="small"
                            onClick={() => setAmendOpen(true)}
                            sx={{
                                fontFamily: poppins, fontWeight: 600, textTransform: 'none', borderRadius: '8px', fontSize: '0.8rem',
                                background: 'linear-gradient(135deg, #6366F1, #4F46E5)',
                            }}
                        >
                            Amend Commission
                        </Button>
                    )}
                </Box>

                {/* How Total Commission is arrived at, spelled out.
                    Worked by hand this reads as 108 short, because the natural reading is to
                    deduct only the AGENT discount. The pool is actually reduced by the whole
                    NEXWAVE GROUP discount — agent + upline + team — since each of those is
                    funded from inside the group (the agent's off PSC, the upline's off DOC,
                    the team's off TOC) rather than by Astern. Astern's own discount comes off
                    earlier, in the base. Printing the line stops the arithmetic being
                    reconstructed from the four separate cards above and landing short.
                    (user 2026-10-23: "webbey discount = agent discount + team discount" /
                     "use nexwave group discount") */}
                {(() => {
                    const base = (Number(sale?.originalPrice) || 0) - (Number(sale?.discountByAstern) || 0);
                    const groupDisc = (Number(sale?.discountByAgent) || 0)
                                    + (Number(sale?.discountByUpline) || 0)
                                    + (Number(sale?.discountByTeam) || 0);
                    const totalLine = commissionLines.find(l => l.commPayType === 'Total Commission');
                    // The stored total is the authority; the rate is recovered from it so the
                    // line can never disagree with the row it is explaining.
                    const total = totalLine ? Number(totalLine.commPay) : null;
                    if (total == null || base <= 0) return null;
                    const ratePct = ((total + groupDisc) / base) * 100;
                    const parts = [
                        ['agent', Number(sale?.discountByAgent) || 0],
                        ['upline', Number(sale?.discountByUpline) || 0],
                        ['team', Number(sale?.discountByTeam) || 0],
                    ].filter(([, v]) => v !== 0);
                    return (
                        <Box sx={{ px: 2, py: 1.5, background: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
                            <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: poppins, mb: 0.5 }}>
                                How Total Commission is worked out
                            </Typography>
                            <Typography sx={{ fontSize: '0.85rem', color: '#0F172A', fontFamily: poppins, fontWeight: 600, lineHeight: 1.7 }}>
                                ({rm(sale?.originalPrice)} <span style={{ color: '#64748B', fontWeight: 500 }}>original</span>
                                {' − '}{rm(sale?.discountByAstern)} <span style={{ color: '#64748B', fontWeight: 500 }}>Astern discount</span>)
                                {' × '}{ratePct.toFixed(2)}%
                                {groupDisc !== 0 && <> {' − '}{rm(groupDisc)} <span style={{ color: '#64748B', fontWeight: 500 }}>Nexwave group discount</span></>}
                                {' = '}<span style={{ color: '#6366F1' }}>{rm(total)}</span>
                            </Typography>
                            {groupDisc !== 0 && (
                                <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontFamily: poppins, mt: 0.5 }}>
                                    Nexwave group discount {rm(groupDisc)} = {parts.map(([k, v]) => `${k} ${rm(v)}`).join(' + ')}
                                    {' '}— each funded by whoever granted it, so the company residual is unchanged.
                                </Typography>
                            )}
                        </Box>
                    );
                })()}

                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {['Type', 'Recipient', 'Rate (%)', 'Amount (RM)', 'Formula', '1st Pay', '2nd Pay', 'Status', 'Remarks'].map(h => (
                                    <TableCell key={h} sx={{
                                        backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0',
                                        textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600,
                                        letterSpacing: '0.5px', color: '#64748B', fontFamily: poppins, padding: '8px 10px',
                                    }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {commissionLines?.map((line, i) => {
                                const typeColor = TYPE_COLORS[line.commPayType] || '#64748B';
                                return (
                                    <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#F8FAFC' } }}>
                                        <TableCell sx={{ py: 0.75, px: 1.25, fontFamily: poppins, fontSize: '0.8rem' }}>
                                            <Chip label={line.commPayType} size="small" sx={{
                                                height: 20, fontSize: '0.68rem', fontWeight: 600, fontFamily: poppins,
                                                backgroundColor: `${typeColor}18`, color: typeColor, border: `1px solid ${typeColor}40`,
                                            }} />
                                        </TableCell>
                                        <TableCell sx={{ py: 0.75, px: 1.25, fontFamily: poppins, fontSize: '0.8rem', color: '#374151' }}>
                                            {line.paymentToName || '—'}
                                        </TableCell>
                                        <TableCell sx={{ py: 0.75, px: 1.25, fontFamily: poppins, fontSize: '0.8rem', color: '#374151' }}>
                                            {line.rate ? <strong>{line.rate}%</strong> : '—'}
                                        </TableCell>
                                        {/* Amount may be NEGATIVE when an Upline/Team discount exceeds
                                            this recipient's override — that is CORRECT, never clamped,
                                            and is shown in red so it reads as an amount owed back. */}
                                        <TableCell sx={{ py: 0.75, px: 1.25, fontFamily: poppins, fontSize: '0.8rem', fontWeight: 600, color: '#0F172A', ...negSx(line.commPay) }}>
                                            {line.commPay != null ? rm(line.commPay) : '—'}
                                        </TableCell>
                                        <TableCell sx={{ py: 0.75, px: 1.25, fontFamily: poppins, fontSize: '0.68rem', color: '#94A3B8', maxWidth: 280 }}>
                                            {line.formula || '—'}
                                        </TableCell>
                                        <TableCell sx={{ py: 0.75, px: 1.25, fontFamily: poppins, fontSize: '0.8rem' }}>
                                            {line.firstPayment != null ? (
                                                <Box>
                                                    <Typography sx={{ fontSize: '0.75rem', fontFamily: poppins, color: '#374151', ...negSx(line.firstPayment) }}>{rm(line.firstPayment)}</Typography>
                                                    <PaymentProgress paid={line.firstPaymentPaid || 0} total={line.firstPayment} />
                                                </Box>
                                            ) : '—'}
                                        </TableCell>
                                        <TableCell sx={{ py: 0.75, px: 1.25, fontFamily: poppins, fontSize: '0.8rem' }}>
                                            {line.secondPayment != null ? (
                                                <Box>
                                                    <Typography sx={{ fontSize: '0.75rem', fontFamily: poppins, color: '#374151', ...negSx(line.secondPayment) }}>{rm(line.secondPayment)}</Typography>
                                                    <PaymentProgress paid={line.secondPaymentPaid || 0} total={line.secondPayment} />
                                                </Box>
                                            ) : '—'}
                                        </TableCell>
                                        <TableCell sx={{ py: 0.75, px: 1.25, fontFamily: poppins, fontSize: '0.8rem', color: '#374151' }}>
                                            {line.status || '—'}
                                        </TableCell>
                                        <TableCell sx={{ py: 0.75, px: 1.25, fontFamily: poppins, fontSize: '0.78rem', color: '#94A3B8' }}>
                                            {line.remarks || '—'}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {(!commissionLines || commissionLines.length === 0) && (
                                <TableRow>
                                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 3, fontFamily: poppins, fontSize: '0.85rem', color: '#94A3B8' }}>
                                        No commission lines found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </Paper>

            {/* Changes History — full details for Accountant/Super Admin, remarks-only for agents */}
            {(() => {
                const isPrivileged = user?.roles?.includes('Accountant') || user?.roles?.includes('Super Admin');
                return (
                    <Paper sx={{ p: 3, borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', mt: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontFamily: "'Poppins', sans-serif" }}>
                            {isPrivileged ? 'Changes History' : 'Remarks'}
                        </Typography>
                        <LogViewer salesId={parseInt(salesId)} readOnly hideChanges={!isPrivileged} />
                    </Paper>
                );
            })()}

            {/* Amend Commission Dialog */}
            <AmendCommissionDialog
                open={amendOpen}
                onClose={() => setAmendOpen(false)}
                onSaved={fetchDetail}
                salesId={parseInt(salesId)}
                commissionLines={commissionLines}
                finalPrice={sale?.finalPrice}
                originalPrice={sale?.originalPrice}
                discountByAstern={sale?.discountByAstern}
                discountByAgent={sale?.discountByAgent}
                discountByUpline={sale?.discountByUpline}
                discountByTeam={sale?.discountByTeam}
                coAgentName={sale?.coAgentName}
            />
        </div>
    );
}
